/**
 * UsageExportModal.jsx — fund-level raw usage download.
 *
 * Pick a fund, get a CSV of every product event for every user at that fund
 * (date, feature, user email — the clients-app /usage "Download Raw Data"
 * format) — sub-funds included by default, so "Millennium" covers its pods
 * in one pull. The heavy lifting is server-side (routes/usageExport.js
 * streams from the Snowflake copy of the Amplitude export); this dialog just
 * scopes the request and saves the blob.
 *
 * Props:
 *   firms    — full firm list ({ firm_id, firm_name, parent_firm_id })
 *   getToken — auth
 *   onClose  — () => void
 */

import { useMemo, useState } from 'react';
import { downloadFirmUsageCsvBlob } from '../api/backend.js';
import FirmCombobox from './FirmCombobox.jsx';

const _dateStr = (d) => d.toISOString().slice(0, 10);

export default function UsageExportModal({ firms = [], getToken, onClose }) {
  const [firmId, setFirmId] = useState('');
  const [includeSubfunds, setIncludeSubfunds] = useState(true);
  const [start, setStart] = useState(() => _dateStr(new Date(Date.now() - 179 * 86_400_000)));
  const [end, setEnd] = useState(() => _dateStr(new Date()));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const firm = useMemo(() => firms.find((f) => f.firm_id === firmId) ?? null, [firms, firmId]);
  const subfunds = useMemo(
    () => (firmId ? firms.filter((f) => f.parent_firm_id === firmId) : []),
    [firms, firmId],
  );

  const rangeValid = start && end && start <= end;

  async function download() {
    if (!firmId || !rangeValid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const blob = await downloadFirmUsageCsvBlob(getToken, firmId, { start, end, includeSubfunds });
      const safeName = String(firm?.firm_name ?? firmId).replace(/[^\w.-]+/g, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_usage_data_${start}_to_${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      onClose?.();
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  return (
    <div className="merge-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}>
      <div className="merge-modal">
        <div className="merge-modal__title">Raw usage export</div>

        <div className="merge-modal__row">
          <span className="merge-modal__label">Fund</span>
          <FirmCombobox firms={firms} value={firmId} onChange={(id) => setFirmId(id)} placeholder="Search funds…" />
        </div>

        <div className="merge-modal__row">
          <span className="merge-modal__label">Dates</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <input
              type="date"
              value={start}
              max={end || undefined}
              onChange={(e) => setStart(e.target.value)}
              style={{ fontSize: 13, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
            />
            <span style={{ color: 'var(--text-3)', fontSize: 13 }}>to</span>
            <input
              type="date"
              value={end}
              min={start || undefined}
              onChange={(e) => setEnd(e.target.value)}
              style={{ fontSize: 13, padding: '5px 8px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)' }}
            />
          </div>
        </div>

        <div className="merge-modal__row">
          <span className="merge-modal__label">Scope</span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={includeSubfunds} onChange={(e) => setIncludeSubfunds(e.target.checked)} />
            Include sub-funds
            {firmId && includeSubfunds && (
              <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
                {subfunds.length ? `— ${subfunds.length} under ${firm?.firm_name}` : '— none linked to this fund'}
              </span>
            )}
          </label>
        </div>

        <div className="merge-modal__note">
          One row per product event (Chat, Protege, Dashboard, Stock Dashboards, Sheets, Vault,
          Live Events, Workflows — custom workflows and their scheduled emails included) for every
          user linked to the fund{includeSubfunds ? ' or its sub-funds' : ''}, matched across email
          changes. Stock-dashboard views are deduped to one per user, ticker, and minute. Data lags
          live usage by up to ~2h.
        </div>

        {error && (
          <div style={{ color: 'var(--c-red)', fontSize: 12.5 }}>{error}</div>
        )}

        <div className="merge-modal__footer">
          <button className="btn btn--ghost btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--primary btn--sm" onClick={download} disabled={!firmId || !rangeValid || busy}>
            {busy ? 'Exporting…' : 'Download CSV'}
          </button>
        </div>
      </div>
    </div>
  );
}
